<script lang="ts">
import { defineComponent as __MACROS_defineComponent } from "vue";
export default /*#__PURE__*/ __MACROS_defineComponent({
  name: 'AuthenticationLogin',
});
</script>
<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { FormInstance } from 'element-plus';
import Motion from './motion';
import LanguageToggle from './language-toggle.vue';
import type { AuthenticationLoginProps, LoginFormData } from './types';

;

const props = withDefaults(defineProps<AuthenticationLoginProps>(), {
  loading: false,
  showVerifyCode: true,
  showLanguageSwitch: true,
  title: '',
  subtitle: '',
  logoSrc: '',
  illustrationSrc: '',
  backgroundImage: '',
  verifyCodeImage: '',
});

const emit = defineEmits<{
  submit: [data: LoginFormData];
  'update:verifyCodeImage': [value: string];
  refreshVerifyCode: [];
  languageChange: [locale: string];
}>();

const { t } = useI18n();
const ruleFormRef = ref<FormInstance>();

const formData = ref<LoginFormData>({
  username: '',
  password: '',
  verifyCode: '',
});

// 处理表单提交
const handleSubmit = async () => {
  if (!ruleFormRef.value) return;

  await ruleFormRef.value.validate((valid) => {
    if (valid) {
      emit('submit', { ...formData.value });
    }
  });
};

// 刷新验证码
const handleRefreshVerifyCode = () => {
  emit('refreshVerifyCode');
};

// 切换语言事件转发
const handleLanguageChange = (newLocale: string) => {
  emit('languageChange', newLocale);
  // 注意：formRules 的 watch 会自动清除校验状态
};

// 表单验证规则
const formRules = computed(() => {
  const rules: any = {};

  props.formSchema.forEach((field) => {
    if (field.rules) {
      rules[field.field] = field.rules;
    }
  });

  return rules;
});

// 监听 formRules 变化，在 rules 变化后清除校验状态
// 这主要用于语言切换时，防止触发校验
watch(formRules, () => {
  // 使用 setTimeout 延迟清除，确保在 Element Plus 内部校验完成后再清除
  setTimeout(() => {
    ruleFormRef.value?.clearValidate();
  }, 0);
});

// 获取当前年份
const currentYear = new Date().getFullYear();
</script>

<template>
  <div class="login-container flex justify-center">
    <div class="login-container-box">
      <div class="login flex">
        <!-- 左侧插图区域 -->
        <div class="login-left">
          <img v-if="logoSrc" :src="logoSrc" class="logo" alt="logo" />
          <img
            v-if="illustrationSrc"
            :src="illustrationSrc"
            class="illustration"
            alt="illustration"
          />
          <slot name="illustration" />
        </div>

        <!-- 右侧表单区域 -->
        <div class="login-box flex">
          <div class="login-form">
            <!-- 标题 -->
            <Motion>
              <div v-if="title" class="welcome-title">
                {{ title }}
              </div>
              <div v-if="subtitle" class="welcome-title">
                {{ subtitle }}
              </div>
              <slot name="title" />
            </Motion>

            <!-- 登录表单 -->
            <el-form
              ref="ruleFormRef"
              :model="formData"
              :rules="formRules"
              size="default"
              style="display: flex; flex-direction: column; width: 100%; margin-top: 40px"
              label-position="top"
              @keyup.enter="handleSubmit"
            >
              <!-- 动态渲染表单项 -->
              <Motion
                v-for="(field, index) in formSchema"
                :key="field.field"
                :delay="100 + index * 50"
              >
                <el-form-item :prop="field.field" :label="field.label" inline-message show-message>
                  <!-- 用户名输入框 -->
                  <el-input
                    v-if="field.component === 'Input'"
                    v-model="formData[field.field]"
                    :placeholder="field.placeholder"
                    :type="field.componentProps?.type || 'text'"
                    :show-password="field.componentProps?.showPassword"
                    clearable
                  />

                  <!-- 验证码输入框 -->
                  <el-input
                    v-else-if="field.component === 'InputVerifyCode'"
                    v-model="formData[field.field]"
                    :placeholder="field.placeholder"
                    :maxlength="field.componentProps?.maxlength"
                    clearable
                    class="verifyCode"
                  >
                    <template #append>
                      <slot name="verifyCode">
                        <img
                          v-if="verifyCodeImage"
                          class="verify-code"
                          :src="verifyCodeImage"
                          :alt="t('login.captcha')"
                          @click="handleRefreshVerifyCode"
                        />
                      </slot>
                    </template>
                  </el-input>
                </el-form-item>
              </Motion>

              <!-- 错误提示插槽 -->
              <slot name="error" />

              <!-- 提交按钮 -->
              <Motion :delay="100 + formSchema.length * 50">
                <el-form-item style="height: 46px">
                  <slot name="submitButton" :submit="handleSubmit" :loading="loading">
                    <el-button
                      class="w-full login-btn"
                      style="height: 100%; line-height: 20px"
                      type="primary"
                      :loading="loading"
                      @click="handleSubmit"
                    >
                      {{ t('login.loginBtn') }}
                    </el-button>
                  </slot>
                </el-form-item>
              </Motion>
            </el-form>

            <!-- 额外内容插槽 -->
            <slot name="extra" />
          </div>
        </div>
      </div>
    </div>

    <!-- 页脚 -->
    <footer style="position: fixed; bottom: 10px; font-size: 12px; color: #fff; text-align: center">
      <slot name="footer">© 2022-{{ currentYear }} PAX All Rights Reserved</slot>
    </footer>

    <!-- 装饰元素 -->
    <div class="bird">
      <slot name="decoration" />
    </div>

    <!-- 语言切换 -->
    <LanguageToggle :show="showLanguageSwitch" @change="handleLanguageChange">
      <template
        #default="{ locale: currentLocale, isChinese, isEnglish, switchToChinese, switchToEnglish }"
      >
        <slot
          name="languageSwitch"
          :locale="currentLocale"
          :is-chinese="isChinese"
          :is-english="isEnglish"
          :change="handleLanguageChange"
          :switch-to-chinese="switchToChinese"
          :switch-to-english="switchToEnglish"
        />
      </template>
    </LanguageToggle>
  </div>
</template>

<style lang="scss" scoped>
// 组件内部样式保持最小化，主要样式由 @klondpay/styles 提供
:deep(.el-input-group__append, .el-input-group__prepend) {
  padding: 0;
}

.logo-img {
  margin: 0 auto;
}

.illustration {
  width: 350px;
  height: 303px;
}

.verify-code {
  width: 88px;
  height: 36px;
  cursor: pointer;
}

.error-msg {
  margin-top: -8px;
  margin-bottom: -12px;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 12px;
  color: var(--el-color-danger);
  text-align: left;
}

.login-left {
  padding: 0 45px 88px 60px;
}
</style>
